// Final job descriptions will replace `problem`, `work` and `you` for each role.
export const ROLES = [
  {
    slug: "field-robotics-engineer",
    title: "Field Robotics Engineer",
    discipline: "Robotics",
    line: "Make robots genuinely useful inside a working ship at sea.",
    problem: "Machinery spaces are cramped, hot, moving, wet and full of equipment designed for human hands. A robot aboard an unmanned ship is the vessel's only way to physically touch the world: to open, close, reset, tighten, clear, replace and repair.",
    work: [
      "Mobile manipulation in machinery spaces on a moving platform",
      "Integrating external robot platforms with the ship's own sensing and control",
      "Task design: redesigning interventions so robots can do them reliably",
      "Field testing aboard real vessels and in realistic mock-ups",
    ],
    you: [
      "Have shipped robots that work outside the lab",
      "Think in systems: perception, planning, control, hardware and failure modes",
      "Want physical consequences for your work",
    ],
  },
  {
    slug: "naval-architect",
    title: "Naval Architect",
    discipline: "Naval architecture",
    line: "Design cargo ships that never need to accommodate people.",
    problem: "Every cargo ship today is shaped in part by the people who live aboard. Remove that requirement and the vessel itself can change: layout, access, structure, systems and the trade-offs between size, speed and endurance.",
    work: [
      "Vessel architectures for unmanned operation across cargo types",
      "Machinery and access arrangements designed for robots",
      "Working with classification societies and shipyards on new concepts",
      "Translating autonomy and robotics requirements into ship design",
    ],
    you: [
      "Know ships deeply, and are impatient with 'how it has always been done'",
      "Can reason from first principles about stability, structure and arrangement",
      "Want to design the first of a new kind of ship",
    ],
  },
  {
    slug: "mechanical-mechatronics-engineer",
    title: "Mechanical / Mechatronics Engineer",
    discipline: "Mechanical / mechatronics",
    line: "Build the mechanisms that let ships run without hands aboard.",
    problem: "Cargo interfaces, latches, tool changers, docking stations and machinery that robots can service: most of it doesn't exist yet in a form that works at sea, for years, without a person nearby to nudge it.",
    work: [
      "Cargo nets and lifting interfaces that any existing crane hook can catch",
      "Robot-serviceable machinery interfaces and modular components",
      "Prototyping, testing and iterating physical hardware quickly",
      "Designing for corrosion, vibration, motion and long service intervals",
    ],
    you: [
      "Love making real mechanisms that survive harsh environments",
      "Move fluently between CAD, the workshop and the test rig",
      "Take ownership from concept through to field trial",
    ],
  },
  {
    slug: "electrical-engineer",
    title: "Electrical Engineer",
    discipline: "Electrical",
    line: "Power, sense and connect a ship that has to look after itself.",
    problem: "An unmanned vessel needs electrical systems that are observable, redundant and recoverable: power distribution, sensor networks, actuation and communications that degrade gracefully instead of failing silently.",
    work: [
      "Distributed sensing and telemetry across the vessel",
      "Fault-tolerant power and control architectures",
      "Integrating sensors, robots and shipboard electrical systems",
      "Designing for marine environments, standards and long-term reliability",
    ],
    you: [
      "Have designed electrical systems that real people depended on",
      "Care about failure modes as much as nominal performance",
      "Want to work across hardware, firmware and systems",
    ],
  },
];

export const DISCIPLINES = [
  "Field robotics",
  "Physical AI / world models / VLA",
  "AI / ML",
  "Controls",
  "Naval architecture",
  "Mechanical / mechatronics",
  "Electrical",
  "Maritime systems & operations",
  "Software & infrastructure",
  "Other",
];

export const PROBLEMS = [
  { k: "Perception at sea", v: "Seeing reliably in fog, glare, spray, darkness and clutter." },
  { k: "GNSS-denied navigation", v: "Knowing where you are when satellites can't tell you." },
  { k: "Robots in machinery spaces", v: "Manipulation on a moving platform, in spaces built for people." },
  { k: "Cargo without deck crew", v: "Nets and lifting gear any crane hook can catch, with nobody on deck." },
  { k: "Fault tolerance", v: "Detecting, isolating and recovering, far from any port." },
  { k: "Remote operations", v: "Giving people in the ROC the right context at exactly the right moment." },
];

export const ETHOS = [
  { k: "Physical consequences", v: "Your work leaves the screen and goes to sea. It has to work." },
  { k: "Ownership, end to end", v: "Own problems, not tickets: from first principles to sea trial." },
  { k: "Redesign, don't imitate", v: "We rethink the task around machines instead of copying the sailor." },
  { k: "Integrate the best", v: "We build with great partners and invent only where we must." },
];
